/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useSelfServedClients } from '../../services/queries/useSelfServedClients';
import Checkbox from '../UI/Toggles/Checkbox/Checkbox';

interface SelfServedClientSelectorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  defaultValue?: string | null;
  disabled?: boolean;
  onValueChange?: (value: string | null) => void;
}

const SelfServedClientSelector = ({
  control,
  name,
  defaultValue = null,
  disabled = false,
  onValueChange,
}: SelfServedClientSelectorProps) => {
  const [isEnabled, setIsEnabled] = useState(!!defaultValue);
  const { data: clients = [], isLoading } = useSelfServedClients();

  // Update isEnabled when defaultValue changes (for edit mode)
  useEffect(() => {
    setIsEnabled(!!defaultValue);
  }, [defaultValue]);

  return (
    <div className="space-y-4">
      {/* Checkbox to enable/disable self-served client selection */}
      <div className="flex items-center space-x-3">
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={({ field: { onChange } }) => (
            <Checkbox
              id={`${name}_enabled`}
              name={`${name}_enabled`}
              type="tick"
              checked={isEnabled}
              onChange={e => {
                const isChecked = e.target.checked;
                setIsEnabled(isChecked);

                if (!isChecked) {
                  // Unchecked: clear value and send null
                  onChange(null);
                  if (onValueChange) {
                    onValueChange(null);
                  }
                }
                // If checked, don't change value yet - user will select from dropdown
              }}
              disabled={disabled}
              className="checked:!bg-primary !border-primary flex"
            />
          )}
        />
        <label htmlFor={`${name}_enabled`} className="text-sm font-medium text-gray-700">
          Assign to Self-Served Client
        </label>
      </div>

      {/* Dropdown that appears when checkbox is checked */}
      {isEnabled && (
        <div className="ml-6">
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field: { onChange, value } }) => (
              <div>
                <label
                  htmlFor={`${name}_select`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Select Self-Served Client
                </label>
                <select
                  id={`${name}_select`}
                  value={value || ''}
                  onChange={e => {
                    const selectedValue = e.target.value || null;
                    onChange(selectedValue);
                    if (onValueChange) {
                      onValueChange(selectedValue);
                    }
                  }}
                  disabled={disabled || isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a client...</option>
                  {clients
                    .filter(client => client.isActive)
                    .map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                </select>
                {isLoading && (
                  <p className="mt-1 text-sm text-gray-500">Loading clients...</p>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SelfServedClientSelector;
